import React, { useRef, useState } from "react";
import { CheckCircle, Circle, Clock, UploadSimple, FilePdf, ImageSquare, Trash, Truck, PaperclipHorizontal, PencilSimple } from "@phosphor-icons/react";
import { formatDateTime } from "@/lib/format";
import { API } from "@/lib/api";
import api from "@/lib/api";
import { authFileClick } from "@/lib/openFile";
import AuthImage from "@/components/AuthImage";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const KIND_LABEL = {
  purchase_order: "Purchase Order (PO)",
  delivery_order: "Delivery Order (DO)",
  production_photo: "Production photo",
  attachment: "Attachment",
};

function isImage(att) {
  return (att?.content_type || "").startsWith("image/");
}

function StageAttachment({ att, orderId, stageKey, editable, onDeleted }) {
  const href = `${API}/files/${att.storage_path}`;
  const kind = att.kind || "attachment";
  const [removing, setRemoving] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Remove "${att.filename || 'this attachment'}"?`)) return;
    setRemoving(true);
    try {
      await api.delete(`/orders/${orderId}/timeline/${stageKey}/attachments/${att.file_id}`);
      onDeleted?.();
    } catch (e) {
      toast.error("Failed");
    }
    setRemoving(false);
  };

  if (isImage(att)) {
    return (
      <div className="group relative w-24 h-24 border border-zinc-200 overflow-hidden bg-zinc-50" data-testid={`stage-attachment-${att.file_id}`}>
        <a href={href} target="_blank" rel="noreferrer" onClick={authFileClick(att.storage_path)} className="block w-full h-full">
          <AuthImage
            path={att.storage_path}
            alt={att.filename || "photo"}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            fallback={<div className="w-full h-full bg-zinc-200 grid place-items-center"><ImageSquare size={24} className="text-zinc-400" /></div>}
          />
        </a>
        {editable && (
          <button
            onClick={remove}
            disabled={removing}
            data-testid={`stage-attachment-remove-${att.file_id}`}
            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 bg-red-500 hover:bg-red-600 text-white p-1 transition-opacity disabled:opacity-40"
          >
            <Trash size={12} weight="fill" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 border border-zinc-200 bg-white px-3 py-2 text-sm hover:border-[#0047AB] hover:bg-zinc-50 transition-colors" data-testid={`stage-attachment-${att.file_id}`}>
      <a href={href} target="_blank" rel="noreferrer" onClick={authFileClick(att.storage_path)} className="flex items-center gap-2 min-w-0 flex-1 text-zinc-900 hover:text-[#0047AB]">
        <FilePdf size={18} className="text-[#B22222] shrink-0" weight="duotone" />
        <div className="min-w-0">
          <div className="truncate font-medium">{att.filename || "Document"}</div>
          <div className="mt-tiny text-zinc-400">{KIND_LABEL[kind] || kind}</div>
        </div>
      </a>
      {editable && (
        <button
          onClick={remove}
          disabled={removing}
          data-testid={`stage-attachment-remove-${att.file_id}`}
          className="text-red-500 hover:bg-red-50 p-1.5 disabled:opacity-40"
        >
          <Trash size={14} />
        </button>
      )}
    </div>
  );
}

function StageUploader({ orderId, stageKey, onUploaded }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orders/${orderId}/timeline/${stageKey}/attachment`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Attached ${file.name}`);
      if (ref.current) ref.current.value = "";
      onUploaded?.();
    } catch (err) {
      toast.error("Upload failed");
    }
    setBusy(false);
  };

  const helper =
    stageKey === "ORDER_RECEIVED"
      ? "Attach PO (PDF)"
      : stageKey === "DELIVERED"
      ? "Attach signed DO (PDF)"
      : stageKey === "SHIPPED"
      ? "Attach BOL / dispatch note"
      : "Attach production photo or PDF";

  return (
    <label
      data-testid={`stage-upload-${stageKey}`}
      className="inline-flex items-center gap-2 border border-dashed border-zinc-300 hover:border-[#0047AB] hover:bg-zinc-50 px-3 py-2 text-xs font-medium cursor-pointer transition-colors"
    >
      <UploadSimple size={14} className="text-[#0047AB]" />
      <span>{busy ? "Uploading…" : helper}</span>
      <input
        ref={ref}
        type="file"
        accept=".pdf,image/*"
        onChange={upload}
        className="hidden"
        data-testid={`stage-upload-input-${stageKey}`}
        disabled={busy}
      />
    </label>
  );
}

function TrackingField({ orderId, stage, editable, onSaved }) {
  const [value, setValue] = useState(stage.tracking_details || "");
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { setValue(stage.tracking_details || ""); }, [stage.tracking_details]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/orders/${orderId}/timeline/${stage.key}`, { tracking_details: value });
      toast.success("Tracking updated");
      onSaved?.();
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  if (!editable) {
    if (!value) return null;
    const isLink = /^https?:\/\//i.test(value);
    return (
      <div className="mt-3 flex items-center gap-2 border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm" data-testid={`stage-tracking-${stage.key}`}>
        <Truck size={16} className="text-[#0047AB]" weight="duotone" />
        {isLink ? (
          <a href={value} target="_blank" rel="noreferrer" className="text-[#0047AB] hover:underline break-all">{value}</a>
        ) : (
          <span className="text-zinc-800">{value}</span>
        )}
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-2" data-testid={`stage-tracking-input-${stage.key}`}>
      <Truck size={16} className="text-[#0047AB]" weight="duotone" />
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Tracking link or truck number"
        className="h-9 border-zinc-200 flex-1 text-sm"
      />
      <button
        onClick={save}
        disabled={busy}
        data-testid={`stage-tracking-save-${stage.key}`}
        className="border border-zinc-200 hover:border-[#0047AB] hover:text-[#0047AB] px-3 py-1.5 text-xs font-medium disabled:opacity-50"
      >
        {busy ? "…" : "Save"}
      </button>
    </div>
  );
}

export default function OrderTimeline({ timeline, current_status, orderId, editable = false, onChanged }) {
  const currentIdx = timeline.findIndex((s) => s.key === current_status);

  return (
    <div className="relative" data-testid="order-timeline">
      {timeline.map((step, i) => {
        const isCurrent = i === currentIdx;
        const isDone = step.completed && !isCurrent;
        const isPending = !step.completed;
        const atts = step.attachments || [];
        const showUploader = editable && orderId;
        const showTracking = step.key === "SHIPPED" && (editable || step.tracking_details);
        return (
          <div key={step.key} className="relative pl-12 pb-8 last:pb-0" data-testid={`timeline-step-${step.key}`}>
            {i < timeline.length - 1 && (
              <div
                className={`absolute left-[15px] top-8 bottom-0 w-[2px] ${
                  step.completed && timeline[i + 1].completed
                    ? "bg-[#0047AB]"
                    : step.completed
                    ? "bg-gradient-to-b from-[#0047AB] to-zinc-200"
                    : "bg-zinc-200"
                }`}
              />
            )}
            <div className="absolute left-0 top-0 flex items-center justify-center">
              {isCurrent ? (
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-[#FF5A1F] animate-ping opacity-40" />
                  <div className="relative w-8 h-8 rounded-full bg-[#FF5A1F] grid place-items-center">
                    <Clock size={16} weight="fill" className="text-white" />
                  </div>
                </div>
              ) : isDone ? (
                <div className="w-8 h-8 rounded-full bg-[#0047AB] grid place-items-center">
                  <CheckCircle size={18} weight="fill" className="text-white" />
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full border-2 border-zinc-200 bg-white grid place-items-center">
                  <Circle size={12} className="text-zinc-300" />
                </div>
              )}
            </div>

            <div>
              <div className="flex items-baseline justify-between gap-3">
                <div className={`font-display text-lg font-bold ${isPending ? "text-zinc-400" : "text-zinc-900"}`}>{step.label}</div>
                {step.completed_at && (
                  <div className="mt-tiny text-zinc-500 font-mono-mt">{formatDateTime(step.completed_at)}</div>
                )}
              </div>
              {step.notes && <div className="mt-2 text-sm text-zinc-600 leading-relaxed">{step.notes}</div>}
              {isCurrent && <div className="mt-2 mt-tiny text-[#FF5A1F]">Current stage</div>}

              {showTracking && (
                <TrackingField orderId={orderId} stage={step} editable={editable} onSaved={onChanged} />
              )}

              {(atts.length > 0 || showUploader) && (
                <div className="mt-3">
                  {atts.length > 0 && (
                    <div className={`flex flex-wrap gap-2 mb-2 ${atts.some(isImage) ? "" : "flex-col"}`}>
                      {atts.map((a) => (
                        <StageAttachment
                          key={a.file_id}
                          att={a}
                          orderId={orderId}
                          stageKey={step.key}
                          editable={editable}
                          onDeleted={onChanged}
                        />
                      ))}
                    </div>
                  )}
                  {showUploader && (
                    <StageUploader orderId={orderId} stageKey={step.key} onUploaded={onChanged} />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
